import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';

export interface PrescriptionDataInput {
    sphere: number;
    cylinder: number;
    axis?: number;
    add?: number;
}

export interface LensProduct {
    id: string;
    visionType: string;
    feature: string;
    isActive: boolean;
    sortOrder: number;
    translations: { locale: string; name: string; description?: string }[];
    variants: {
        id: string;
        tier: string;
        refractiveIndex: number;
        price: number;
        costPrice?: number;
        deliveryMinDays: number;
        deliveryMaxDays: number;
        isActive: boolean;
    }[];
}

export interface AlternativeProduct {
    id: string;
    slug: string;
    price: number;
    compareAtPrice?: number;
    gender?: string;
    frameShape?: string;
    frameType?: string;
    translations: { locale: string; name: string }[];
    images: { url: string }[];
}

export interface ResolveGroupResult {
    match: {
        id: string;
        groupNumber: number;
        suggestedIndex: number;
        preferredIndex: number;
        noteEn?: string;
        noteAr?: string;
        lensProducts: LensProduct[];
    } | null;
    alternatives: AlternativeProduct[];
}

export const usePublicLensProducts = () => {
    return useQuery({
        queryKey: ['public', 'lens', 'products'],
        queryFn: async () => {
            const response = await api.get('/lens/products');
            return (response.data.data || response.data) as LensProduct[];
        }
    });
};

export const useResolvePrescriptionGroup = (
    rightEye: PrescriptionDataInput | null,
    leftEye: PrescriptionDataInput | null,
    productId?: string | null
) => {
    return useQuery<ResolveGroupResult>({
        queryKey: ['public', 'lens', 'resolve-group', rightEye, leftEye, productId],
        queryFn: async () => {
            if (!rightEye || !leftEye) return { match: null, alternatives: [] };
            const response = await api.get('/lens/resolve-group', {
                params: {
                    rs: rightEye.sphere,
                    rc: rightEye.cylinder,
                    ls: leftEye.sphere,
                    lc: leftEye.cylinder,
                    ...(productId ? { pid: productId } : {})
                }
            });
            // Support both { data: { match, alternatives } } and legacy flat shape
            const raw = response.data?.data ?? response.data;
            if (raw && 'match' in raw) return raw as ResolveGroupResult;
            // Legacy: the whole object IS the rule
            return { match: raw, alternatives: [] } as ResolveGroupResult;
        },
        enabled: !!rightEye && !!leftEye
    });
};
